<template>
  <computed-field-value
    class="p-2"
    :value="fieldValue"
    :error="fieldError"
    v-if="field.isComputed"
  />
  <div
    class="d-contents"
    v-else-if="field.type === CUSTOM_FIELD_TYPE.TEXT || field.type === CUSTOM_FIELD_TYPE.NUMBER"
  >
    <div class="inline-edit h-100" v-if="editable">
      <input
        class="text-medium h-100 w-100 px-2 input-field"
        ref="input"
        @keyup="lastKey = $event.keyCode"
        @keyup.enter="$event.target.blur()"
        @keyup.esc="cancelEditing"
        @blur="lastKey !== KEY_CODES.ESCAPE && saveFieldValue(textValue)"
        v-model="textValue"
        :type="inputType[field.type]"
        data-prevent-blur="true"
        v-autofocus
      />
    </div>
    <div
      class="h-100 w-100 text-field-value text-left editable-text"
      :title="textValue"
      @click="onTextClick"
      v-else
    >
      <span class="content">{{ textValue }}</span>
    </div>
  </div>
  <div
    class="pointer date-field clearable-field h-100 pt-2 focusable"
    :id="'field-' + initiative.id + '-' + field.id"
    tabindex="0"
    v-else-if="field.type === CUSTOM_FIELD_TYPE.DATE"
  >
    <template>
      <date-value
        :prevent-color-coding="isSettled"
        :value="fieldValue"
        @clear="onDateChange(null)"
        v-if="fieldValue"
      />
      <div class="date-placeholder h-100 text-small text-secondary" v-else>Select</div>
    </template>
    <b-popover
      ref="datePopover"
      custom-class="initiative-cf-field-po"
      triggers="click blur"
      placement="bottom"
      :target="'field-' + initiative.id + '-' + field.id"
    >
      <date-picker :value="fieldValue" @input="onDateChange" />
    </b-popover>
  </div>
  <div
    tabindex="0"
    @click="toggleUserSelector"
    @keydown.enter.stop
    @keydown.space.prevent
    @keyup.space.enter="openUserSelector"
    @keydown.esc.down.up="closeUserSelector"
    class="user-field-wrapper focusable"
    v-else-if="field.type === CUSTOM_FIELD_TYPE.USER"
  >
    <user-field
      :length="22"
      :selected="fieldValue"
      :team-id="teamId"
      :org-id="initiative.organizationId"
      :field-name="field.name"
      ref="userField"
      @change="onUserChange"
    />
  </div>
  <div class="dropdown-cf h-100" v-else-if="field.type === CUSTOM_FIELD_TYPE.DROPDOWN">
    <initiative-dropdown-field @select="saveFieldValue" :field="field" :selected="fieldValue" />
  </div>
</template>

<script>
import DatePicker from '@/components/form/DatePicker'
import ComputedFieldValue from '@/components/initiative/ComputedFieldValue.vue'
import DateValue from '@/components/initiative/DateValue.vue'
import InitiativeDropdownField from '@/components/initiative/InitiativeDropdownField'
import UserField from '@/components/initiative/UserField'
import { CUSTOM_FIELD_TYPE, CUSTOM_FIELD_TYPE_KEY } from '@/constants/customFields'
import events from '@/constants/events'
import { STATUS } from '@/constants/initiative'
import { KEY_CODES } from '@/constants/keyCodes'
import EventBusMixin from '@/mixins/EventBusMixin'
import multiClickHandler from '@/utils/multiClickHandler'
import showAPIError from '@/utils/showAPIError'
import sleep from '@/utils/sleep'
import { BPopover } from 'bootstrap-vue'
import { mapActions } from 'vuex'

export default {
  name: 'InitiativeCustomFieldCell',
  props: ['initiative', 'field'],
  mixins: [EventBusMixin],
  components: {
    ComputedFieldValue,
    DatePicker,
    BPopover,
    UserField,
    InitiativeDropdownField,
    DateValue
  },
  data() {
    const fieldValue = this.getFieldValue()
    return {
      CUSTOM_FIELD_TYPE,
      editable: false,
      lastKey: null,
      inputType: {
        [CUSTOM_FIELD_TYPE.NUMBER]: 'number',
        [CUSTOM_FIELD_TYPE.TEXT]: 'text'
      },
      textValue: fieldValue[CUSTOM_FIELD_TYPE_KEY[this.field.type]],
      KEY_CODES
    }
  },
  computed: {
    teamId() {
      const goalId = this.initiative.goalId
      const goal = this.$store.getters.goalById(goalId)
      return goal?.teamId
    },
    fieldValue() {
      const definition = this.getFieldValue()
      const key = CUSTOM_FIELD_TYPE_KEY[this.field.type]
      return definition[key]
    },
    fieldError() {
      const definition = this.getFieldValue()
      return definition.error
    },
    isSettled() {
      return this.initiative.isClosed || this.initiative.status === STATUS.completed.id
    }
  },
  methods: {
    ...mapActions(['setInitiativeCustomField', 'getInitiative']),
    getFieldValue() {
      const cfs = this.initiative.customFields || []
      return cfs.find(x => x.definitionId === this.field.id) || {}
    },
    onDateChange(value) {
      this.$refs.datePopover.$emit('close')
      this.saveFieldValue(value)
    },
    onUserChange(userId) {
      this.saveFieldValue(userId)
      this.$refs.userField.hidePopover()
    },
    cancelEditing() {
      const fieldValue = this.getFieldValue()
      this.textValue = fieldValue[CUSTOM_FIELD_TYPE_KEY[this.field.type]]
      this.lastKey = KEY_CODES.ESCAPE
      this.editable = false
    },
    onTextClick() {
      multiClickHandler({
        onDoubleClick: () => {
          this.editable = true
        }
      })
    },
    async saveFieldValue(value) {
      try {
        if (value !== this.fieldValue) {
          if (this.field.type === CUSTOM_FIELD_TYPE.NUMBER) {
            value = value ? +value : null
          }
          await this.setInitiativeCustomField({
            orgId: this.initiative.organizationId,
            initiativeId: this.initiative.id,
            field: this.field,
            value
          })
          if (this.field.type === CUSTOM_FIELD_TYPE.NUMBER) {
            // adding a sleep to make sure initiative re-fetch happens after its computed fields are updated.
            await sleep(1000)
          }
          this.eventBusEmit(events.INITIATIVE_CUSTOM_FIELD_VALUE_UPDATED, {
            initiativeId: this.initiative.id,
            fieldId: this.field.id,
            value
          })
        }
        this.editable = false
      } catch (e) {
        showAPIError(e)
        this.$refs.input.focus()
      }
    },
    openUserSelector() {
      this.$refs.userField.showPopover()
    },
    closeUserSelector() {
      this.$refs.userField.hidePopover()
    },
    toggleUserSelector() {
      this.$refs.userField.togglePopover()
    }
  },
  watch: {
    fieldValue() {
      const isTextualField = [CUSTOM_FIELD_TYPE.NUMBER, CUSTOM_FIELD_TYPE.TEXT].includes(
        this.field.type
      )
      if (isTextualField) {
        const fieldValue = this.getFieldValue()
        this.textValue = fieldValue[CUSTOM_FIELD_TYPE_KEY[this.field.type]]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_input.scss';

.d-contents {
  display: contents;
}

.user-field-wrapper {
  padding-top: 7px;
  height: 100%;
}

.focusable:focus {
  box-shadow: inset 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
  background: #edf8fe;
  outline: none;
}

.dropdown-cf /deep/ .dropdown {
  @include dimension(100%, 100%);
}

.text-field-value {
  padding: 8px;

  .content {
    @include lineClamp(2);
  }
}

.date-placeholder {
  padding-top: 1px;
  opacity: 0;
  transition: opacity ease-in 200ms;
}

.date-field {
  display: flex;
  justify-content: center;
  align-items: flex-start;

  &:hover .date-placeholder {
    opacity: 1;
  }
}

input.input-field {
  border-radius: 0;
}
</style>

<style lang="scss">
.initiative-cf-field-po .popover-body {
  padding: 0;
}
</style>
